/**
 * TentativeForm — logs one rung of the D-29 escalation ladder (a push / call attempt) and advances
 * the silence-sweep. The `notification_ref` is an opaque, PHI-safe reference (no health data ever
 * transits the push). A `lu` outcome warns the operator that a read receipt is NOT a read-back
 * (VAL-2). Gated by the caller on `validation.valeurcritique.acknowledge`.
 */
import { useState } from "react";
import { Badge, Button, FormField, Input, Select } from "@/ui";
import { useT } from "@/app";
import { token } from "@/design/theme";
import type { CanalEscalade, ResultatTentative } from "../types";

export interface TentativeFormProps {
  defaultCanal?: CanalEscalade;
  onSubmit: (input: {
    canal: CanalEscalade;
    resultat: ResultatTentative;
    notification_ref?: string;
  }) => Promise<void>;
  submitting?: boolean;
  disabled?: boolean;
}

export function TentativeForm({ defaultCanal = "telephone", onSubmit, submitting, disabled }: TentativeFormProps) {
  const { t } = useT();
  const [canal, setCanal] = useState<CanalEscalade>(defaultCanal);
  const [resultat, setResultat] = useState<ResultatTentative>("envoye");
  const [ref, setRef] = useState("");

  const canalOptions = [
    { value: "telephone", label: t("validation.escalade.canal.telephone") },
    { value: "push_seam2", label: t("validation.escalade.canal.push_seam2") },
    { value: "worklist_biologiste", label: t("validation.escalade.canal.worklist_biologiste") },
  ];
  const resultatOptions: { value: ResultatTentative; label: string }[] = [
    { value: "envoye", label: t("validation.tentative.resultat.envoye") },
    { value: "lu", label: t("validation.tentative.resultat.lu") },
    { value: "sans_reponse", label: t("validation.tentative.resultat.sans_reponse") },
    { value: "echec_technique", label: t("validation.tentative.resultat.echec_technique") },
  ];

  const submit = async () => {
    if (submitting || disabled) return;
    await onSubmit({ canal, resultat, ...(ref.trim() ? { notification_ref: ref.trim() } : {}) });
    setRef("");
    setResultat("envoye");
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      style={{ display: "flex", flexDirection: "column", gap: token.s3 }}
    >
      <FormField label={t("validation.tentative.canal")}>
        {(p) => (
          <Select
            {...p}
            value={canal}
            options={canalOptions}
            onChange={(e) => setCanal(e.target.value as CanalEscalade)}
            disabled={disabled}
          />
        )}
      </FormField>
      <FormField label={t("validation.tentative.resultat")}>
        {(p) => (
          <Select
            {...p}
            value={resultat}
            options={resultatOptions}
            onChange={(e) => setResultat(e.target.value as ResultatTentative)}
            disabled={disabled}
          />
        )}
      </FormField>
      {resultat === "lu" && (
        <Badge tone="warn">{t("validation.tentative.luWarn")}</Badge>
      )}
      <FormField label={t("validation.tentative.ref")} hint={t("validation.tentative.ref.hint")}>
        {(p) => (
          <Input
            {...p}
            mono
            value={ref}
            onChange={(e) => setRef(e.target.value)}
            placeholder={t("validation.id.placeholder")}
            disabled={disabled}
          />
        )}
      </FormField>
      <Button type="submit" variant="secondary" loading={submitting === true} disabled={disabled}>
        {t("validation.tentative.submit")}
      </Button>
    </form>
  );
}
